import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, Input } from "reactstrap";
import notifications from "../../common/Components/Notifications/Notification";
import { canUseRounds } from "../../helpers/karkhanaEnabled";
import { decideJobWork, listJobWork, saveJobWork, voidJobWork } from "../../services/karkhanaApi";
import "../gi-registry/gi-registry.scss";
import { UNIT_LABEL, UNIT_SHORT, parseSarees, previewAmount } from "./jobs/jobModel";
import { readRoster, refreshRoster } from "./punches/roster";
import { fmtDay, rupees, todayIso } from "./rates/rateModel";
import { newRequestId } from "./rounds/entryQueue";
import PersonPicker, { Person } from "./rounds/PersonPicker";
import "./workers/karkhana-workers.scss";
import "./rounds/karkhana-rounds.scss";
import "./pay/karkhana-pay.scss";
import { useKT } from "./i18n/kt";
import PageHeader from "./ui/PageHeader";
import AttendanceTabs from "./punches/AttendanceTabs";
import QuickAddPerson from "./people/QuickAddPerson";
import { askReason } from "./ui/dialog";
import { kp } from "./karkhanaPaths";

const readAuth = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null")?.users || {};
  } catch {
    return {};
  }
};

const STATUS_CHIP: Record<string, string> = {
  PENDING_APPROVAL: "Waiting for approval",
  REJECTED: "Rejected",
};

/**
 * Job work: dyeing (or other work) paid per lot, per colour, per saree… rather than by the hour.
 * Hourly dyeing and warping go in Attendance instead. A supervisor's entry waits for the studio
 * admin, because the rate is agreed money; then it is paid like any other work.
 */
const KarkhanaJobWorkPage = () => {
  const { t } = useKT();
  const history = useHistory();
  const auth = readAuth();
  const role = auth?.role;
  const orgId: string = auth?.organizationResponse?._id || "org";
  const [data, setData] = useState<any>(null);
  const [roster, setRoster] = useState<Person[]>(() => readRoster(orgId));
  const [adding, setAdding] = useState(false);
  const [who, setWho] = useState<Person | null>(null);
  const [newName, setNewName] = useState<string | null>(null);
  const [date, setDate] = useState(todayIso());
  const [activityId, setActivityId] = useState("");
  const [unit, setUnit] = useState("COLOUR");
  const [quantity, setQuantity] = useState("");
  const [rate, setRate] = useState("");
  const [sarees, setSarees] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const res: any = await listJobWork();
      if (res?.success) setData(res.data);
      else notifications({ title: t("Error"), message: t(res?.message || "Could not load job work"), type: "danger" });
    } catch {
      notifications({ title: t("No connection"), message: t("Job work needs a connection."), type: "warning" });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!canUseRounds(role)) {
      history.replace(kp.home());
      return;
    }
    load();
    refreshRoster(orgId).then(setRoster).catch(() => undefined);
  }, [role, history, load, orgId]);

  // Dyeing is the usual job work, so it is picked to start with.
  useEffect(() => {
    if (activityId || !data?.activities?.length) return;
    const dyeing = data.activities.find((a: any) => a.code === "DYEING");
    setActivityId((dyeing || data.activities[0]).id);
  }, [data, activityId]);

  const reset = () => {
    setWho(null);
    setQuantity("");
    setRate("");
    setSarees("");
    setNote("");
  };

  const save = async () => {
    if (!who) {
      notifications({ title: t("Choose the person"), message: t("Search by code or name"), type: "warning" });
      return;
    }
    setBusy("save");
    try {
      const res: any = await saveJobWork({
        clientRequestId: newRequestId(),
        personId: who.id,
        date,
        activityId,
        unit,
        quantity: quantity.trim(),
        rate: rate.trim(),
        sareeNumbers: parseSarees(sarees),
        note: note.trim() || undefined,
      });
      if (!res?.success) {
        notifications({ title: t("Not saved"), message: t(res?.message || "Could not save"), type: "danger" });
        return;
      }
      notifications({
        title: t("Saved"),
        message: res.data.status === "PENDING_APPROVAL" ? t("It is paid once the studio admin approves it.") : t("Added to their pay."),
        type: "success",
      });
      reset();
      setAdding(false);
      load();
    } catch {
      notifications({ title: t("No connection"), message: t("Nothing was recorded. Try again."), type: "warning" });
    } finally {
      setBusy("");
    }
  };

  const decide = async (id: string, approve: boolean) => {
    setBusy(id);
    try {
      const res: any = await decideJobWork(id, approve);
      if (!res?.success) notifications({ title: t("Error"), message: t(res?.message || "Could not update"), type: "danger" });
      load();
    } finally {
      setBusy("");
    }
  };

  const remove = async (id: string) => {
    const why = (await askReason({ title: t("Why is this being removed?") }));
    if (!why || !why.trim()) return;
    setBusy(id);
    try {
      const res: any = await voidJobWork(id, why.trim());
      if (!res?.success) notifications({ title: t("Error"), message: t(res?.message || "Could not remove"), type: "danger" });
      load();
    } finally {
      setBusy("");
    }
  };

  if (!canUseRounds(role)) return null;
  const entries: any[] = data?.entries || [];
  const can = !!data?.canApprove;
  const preview = previewAmount(quantity, rate);

  return (
    <div className="gi-registry-page kw-page kr-rounds">
      <PageHeader title={t("Job work")} back={kp.attendance()} backLabel={t("Back")}>
      </PageHeader>
      <AttendanceTabs active="jobs" jobs={data ? (data.entries || []).length : "…"} />
      <p className="kw-lede">
        {t("Dyeing or other work paid per job, per colour or per saree. Work paid by the hour (dyeing, warping) goes in Attendance; pick the loom or saree it was for.")}
      </p>
      {data?.waiting ? (
        <p className="kw-lede">
          <span className="kw-chip is-review">{t("{{n}} waiting for approval", { n: data.waiting })}</span>
        </p>
      ) : null}

      {!adding ? (
        <div className="kw-toolbar">
          <Button className="ResetBtn" onClick={() => setAdding(true)}>
            {t("Add job work")}
          </Button>
        </div>
      ) : (
        <div className="kw-card">
          <PersonPicker
            value={who}
            onChange={setWho}
            roster={roster}
            label={t("Person")}
            placeholder={t("Code or name, e.g. D_4")}
            onAddNew={(name) => setNewName(name)}
            addLabel="as a new person"
          />
          {newName !== null ? (
            <QuickAddPerson
              initialName={newName}
              role="DYER"
              onCancel={() => setNewName(null)}
              onDone={(p) => {
                setWho({ id: p.id, code: p.code, name: p.name });
                setRoster((r) => [...r, { id: p.id, code: p.code, name: p.name }]);
                setNewName(null);
              }}
            />
          ) : null}
          <label className="kr-label" htmlFor="kj-date">
            {t("Date")}
          </label>
          <Input id="kj-date" type="date" value={date} max={todayIso()} onChange={(e) => e.target.value && setDate(e.target.value)} />
          <span className="kr-label">{t("Work")}</span>
          <div className="kr-chips" role="radiogroup" aria-label={t("Work")}>
            {(data?.activities || []).map((a: any) => (
              <button key={a.id} type="button" role="radio" aria-checked={activityId === a.id} className={`kr-choice${activityId === a.id ? " is-on" : ""}`} onClick={() => setActivityId(a.id)}>
                {t(a.name)}
              </button>
            ))}
          </div>
          <span className="kr-label">{t("Paid by")}</span>
          <div className="kr-chips" role="radiogroup" aria-label={t("Paid by")}>
            {(data?.units || Object.keys(UNIT_LABEL)).map((u: string) => (
              <button key={u} type="button" role="radio" aria-checked={unit === u} className={`kr-choice${unit === u ? " is-on" : ""}`} onClick={() => setUnit(u)}>
                {t(UNIT_LABEL[u] || u)}
              </button>
            ))}
          </div>
          <label className="kr-label" htmlFor="kj-qty">
            {t("How many ({{unit}})", { unit: t(UNIT_SHORT[unit] || unit) })}
          </label>
          <Input id="kj-qty" inputMode="decimal" value={quantity} onChange={(e) => setQuantity(e.target.value)} placeholder={t("e.g. 3")} />
          <label className="kr-label" htmlFor="kj-rate">
            {t("Agreed rate, ₹ per {{unit}}", { unit: t(UNIT_SHORT[unit] || unit) })}
          </label>
          <Input id="kj-rate" inputMode="decimal" value={rate} onChange={(e) => setRate(e.target.value)} placeholder={t("e.g. 150")} />
          <label className="kr-label" htmlFor="kj-sarees">
            {t("Saree numbers (optional)")}
          </label>
          <Input id="kj-sarees" inputMode="numeric" value={sarees} onChange={(e) => setSarees(e.target.value)} placeholder={t("e.g. 18214, 18215")} />
          <label className="kr-label" htmlFor="kj-note">
            {t("Note (optional)")}
          </label>
          <Input id="kj-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("e.g. red and gold for the border")} />
          <div className="kw-toolbar" style={{ marginTop: 10, marginBottom: 0 }}>
            <Button outline onClick={() => { reset(); setAdding(false); }}>
              {t("Cancel")}
            </Button>
            <Button className="ResetBtn" disabled={busy === "save" || !who || !preview || !activityId} onClick={save}>
              {busy === "save" ? t("Saving…") : preview ? t("Save {{amount}}", { amount: rupees(preview) }) : t("Save")}
            </Button>
          </div>
          {!can ? <p className="kw-muted" style={{ marginTop: 8 }}>{t("It is paid once the studio admin approves it.")}</p> : null}
        </div>
      )}

      <h3 className="kr-group-title">{t("Last two weeks")}</h3>
      {!data ? <div className="kw-empty">{t("Loading…")}</div> : null}
      {data && !entries.length ? <div className="kw-empty">{t("No job work yet.")}</div> : null}
      {entries.map((e) => (
        <div key={e.id} className="kw-card">
          <div className="kr-pay__row">
            <strong>
              <span className="kw-code">{e.personCode}</span> {e.personName} · {fmtDay(e.date)}
            </strong>
            <span>{rupees(e.amount)}</span>
          </div>
          <div className="kw-muted">
            {t(e.activityName)} · {e.quantity} {t(UNIT_SHORT[e.unit] || e.unit)} × {rupees(e.rate)}
            {e.sareeNumbers?.length ? ` · ${t("Saree")} ${e.sareeNumbers.join(", ")}` : ""}
            {e.loomCode ? ` · ${e.loomCode}` : ""}
            {e.note ? ` · “${e.note}”` : ""}
          </div>
          <div className="kw-muted">
            {t("Entered by {{name}}", { name: e.enteredByName })}
            {STATUS_CHIP[e.status] ? (
              <>
                {" "}
                <span className="kw-chip is-review">{t(STATUS_CHIP[e.status])}</span>
              </>
            ) : null}
          </div>
          <div className="kw-toolbar" style={{ marginTop: 8, marginBottom: 0 }}>
            {can && e.status === "PENDING_APPROVAL" ? (
              <>
                <Button size="sm" className="ResetBtn" disabled={busy === e.id} onClick={() => decide(e.id, true)}>
                  {t("Approve")}
                </Button>
                <Button size="sm" outline disabled={busy === e.id} onClick={() => decide(e.id, false)}>
                  {t("Reject")}
                </Button>
              </>
            ) : null}
            {e.status !== "REJECTED" ? (
              <Button size="sm" color="link" disabled={busy === e.id} onClick={() => remove(e.id)}>
                {t("Remove")}
              </Button>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
};

export default KarkhanaJobWorkPage;
